import { useState } from "react";

import { api, describeError } from "../api/client";
import type { Bucket } from "../api/types";
import "./buckets.css";

interface Props {
  isAdmin: boolean;
  /**
   * The list the shell already loaded.
   *
   * This page used to fetch `/api/buckets` again on mount, so navigating here
   * asked for a list the shell was already holding — and held a second copy of it
   * in local state, which is the part that actually mattered: after a rename the
   * two could disagree, and which one the user saw depended on which component
   * rendered it.
   */
  buckets: Bucket[];
  /** Refreshes the shell's list, which is now the only copy. */
  onChanged: () => Promise<void>;
  onOpen: (bucket: Bucket) => void;
}

export function Buckets({ isAdmin, buckets, onChanged, onOpen }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function changed() {
    await onChanged().catch((e: unknown) => setError(describeError(e)));
  }

  return (
    <div className="cols">
      <header className="page-head">
        <h1 className="page-title">Colecciones</h1>
        {isAdmin && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setCreating((open) => !open);
              setNotice(null);
              setError(null);
            }}
          >
            {creating ? "Cancelar" : "+ Nueva colección"}
          </button>
        )}
      </header>

      <div className="page-body">
        <p className="page-sub">
          Cada colección agrupa documentos indexados para la búsqueda del asistente.
        </p>

        {creating && (
          <CreateForm
            onCreated={async (bucket) => {
              setCreating(false);
              setNotice(`Colección «${bucket.name}» creada.`);
              await changed();
            }}
            onError={setError}
          />
        )}

        {notice && <p className="cols-notice">{notice}</p>}
        {error && (
          <p className="cols-error" role="alert">
            {error}
          </p>
        )}

        {buckets.length === 0 ? (
          <p className="cols-empty">Todavía no hay ninguna colección.</p>
        ) : (
          <ul className="cols-grid">
            {buckets.map((bucket) => (
              <li key={bucket.id} className="card">
                <div className="card-head">
                  <h2 className="card-title" title={bucket.name}>
                    {bucket.name}
                  </h2>
                  <BucketPill bucket={bucket} />
                </div>
                <p className="card-count">{bucket.document_count.toLocaleString("es-ES")}</p>
                <p className="card-sub">{bucket.description ?? "documentos"}</p>
                <div className="card-foot">
                  <span className="card-meta">
                    {bucket.chunk_count.toLocaleString("es-ES")} fragmentos
                  </span>
                  <button type="button" className="btn btn--ghost" onClick={() => onOpen(bucket)}>
                    Gestionar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function BucketPill({ bucket }: { bucket: Bucket }) {
  if (bucket.document_count === 0) return <span className="pill">Vacía</span>;
  // Documents exist but nothing searchable yet: ingestion is still running.
  if (bucket.chunk_count === 0) return <span className="pill pill--warn">Indexando</span>;
  return <span className="pill pill--ok">Activa</span>;
}

function CreateForm({
  onCreated,
  onError,
}: {
  onCreated: (bucket: Bucket) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const bucket = await api.createBucket({
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
      });
      await onCreated(bucket);
    } catch (e: unknown) {
      onError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="cols-form card" onSubmit={submit}>
      <label>
        <span className="eyebrow">Nombre</span>
        <input
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSlug(suggestSlug(event.target.value));
          }}
          required
          maxLength={256}
          placeholder="Documentación técnica"
        />
      </label>
      <label>
        <span className="eyebrow">Identificador</span>
        <input
          className="cols-slug-input"
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
          required
          maxLength={128}
          pattern="[a-z0-9][a-z0-9-]*"
          title="Minúsculas, números y guiones; debe empezar por letra o número."
          placeholder="tecnica"
        />
      </label>
      <label className="cols-form-wide">
        <span className="eyebrow">Descripción (opcional)</span>
        <input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={2000}
          placeholder="Fichas técnicas, ensayos y certificados"
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={busy}>
        {busy ? "Creando…" : "Crear"}
      </button>
    </form>
  );
}

function suggestSlug(name: string): string {
  // NFD splits accents into combining marks (U+0300–U+036F) so they can be dropped.
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 128);
}

